import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { FiMessageCircle, FiSend } from 'react-icons/fi';
import { useColorContext } from '../../../context/colorContext';
import MathContentLayer from '../../ui/math/MathContentLayer';

const toSafeHex = (hex, fallback) => {
	const value = String(hex || '').trim();
	return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
	const safeHex = toSafeHex(hex, fallback);
	const r = parseInt(safeHex.slice(1, 3), 16);
	const g = parseInt(safeHex.slice(3, 5), 16);
	const b = parseInt(safeHex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const chatInputContainsLatex = (value = '') => {
	const text = String(value || '');
	if (!text) return false;
	if (/\$\$[\s\S]*?\$\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]|\\begin\{[a-zA-Z*]+\}|\\[a-zA-Z]+(?:\{[^}]*\})?/.test(text)) return true;
	let count = 0;
	for (let i = 0; i < text.length; i++) {
		if (text[i] === '$' && (i === 0 || text[i - 1] !== '\\')) count++;
	}
	return count >= 2;
};

const AIChatFloatingButton = ({
	onClick,
	title = 'Open Plynote assistant',
	ariaLabel = 'Open Plynote assistant',
	icon,
	style = {},
}) => {
	const { activeAccent, bgPrimary, bgSecondary, textPrimary } = useColorContext();
	const [isHovered, setIsHovered] = useState(false);
	const {
		transform: customTransform,
		transition: customTransition,
		...restStyle
	} = style || {};

	const safeAccent = toSafeHex(activeAccent, '#3B82F6');
	const safeBgPrimary = toSafeHex(bgPrimary, '#101114');
	const safeBgSecondary = toSafeHex(bgSecondary, '#161A21');
	const safeTextPrimary = toSafeHex(textPrimary, '#F2F5FA');
	const buttonWidth = isHovered ? '60px' : '52px';

	const iconNode = icon || <FiMessageCircle size={21} strokeWidth={2.1} />;

	const backgroundGradient = useMemo(() => {
		return `linear-gradient(155deg, ${hexToRgba(safeAccent, 0.3, safeAccent)} 0%, ${hexToRgba(safeBgSecondary, 0.98, safeBgSecondary)} 35%, ${hexToRgba(safeBgPrimary, 0.98, safeBgPrimary)} 100%)`;
	}, [safeAccent, safeBgPrimary, safeBgSecondary]);

	return (
		<button
			type="button"
			onClick={onClick}
			title={title}
			aria-label={ariaLabel}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsHovered(true)}
			onBlur={() => setIsHovered(false)}
			style={{
				position: 'fixed',
				top: '50%',
				right: '0',
				transform: customTransform || 'translateY(-50%)',
				zIndex: 10000,
				width: buttonWidth,
				height: '52px',
				border: `1px solid ${hexToRgba(safeAccent, 0.68, safeAccent)}`,
				borderRight: 'none',
				borderTopLeftRadius: '14px',
				borderBottomLeftRadius: '14px',
				borderTopRightRadius: '0',
				borderBottomRightRadius: '0',
				background: backgroundGradient,
				color: safeTextPrimary,
				display: 'grid',
				placeItems: 'center',
				cursor: 'pointer',
				fontSize: '21px',
				lineHeight: 1,
				overflow: 'hidden',
				boxShadow: isHovered
					? `0 16px 28px ${hexToRgba(safeBgPrimary, 0.5, safeBgPrimary)}, 0 0 0 1px ${hexToRgba(safeAccent, 0.55, safeAccent)} inset`
					: `0 10px 20px ${hexToRgba(safeBgPrimary, 0.4, safeBgPrimary)}, 0 0 0 1px ${hexToRgba(safeAccent, 0.4, safeAccent)} inset`,
				transition: `${customTransition ? `${customTransition}, ` : ''}width 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 220ms ease, border-color 220ms ease`,
				...restStyle,
			}}
		>
			<span
				aria-hidden="true"
				style={{
					display: 'grid',
					placeItems: 'center',
					color: safeTextPrimary,
					filter: `drop-shadow(0 2px 8px ${hexToRgba(safeAccent, 0.28, safeAccent)})`,
				}}
			>
				{iconNode}
			</span>
		</button>
	);
};

export const AIChatInputBox = ({
	value,
	onChange,
	onSubmit,
	placeholder = 'Ask your question...',
	style = {},
	maxLength = 6000,
}) => {
	const { activeAccent, bgPrimary, bgSecondary, textPrimary, textSecondary } = useColorContext();
	const safeAccent = toSafeHex(activeAccent, '#3B82F6');
	const safeBgPrimary = toSafeHex(bgPrimary, '#101114');
	const safeBgSecondary = toSafeHex(bgSecondary, '#161A21');
	const safeTextPrimary = toSafeHex(textPrimary, '#F2F5FA');
	const safeTextSecondary = toSafeHex(textSecondary, '#A8B4C8');

	const isControlled = value !== undefined;
	const [internalValue, setInternalValue] = useState(String(value || ''));
	const [hasLatex, setHasLatex] = useState(chatInputContainsLatex(value || ''));
	const [interpretationTick, setInterpretationTick] = useState(0);
	const [isInputFocused, setIsInputFocused] = useState(false);
	const [isSendHovered, setIsSendHovered] = useState(false);
	const [textareaHeight, setTextareaHeight] = useState(100);
	const [isTextareaOverflowing, setIsTextareaOverflowing] = useState(false);
	const textareaRef = useRef(null);
	const TEXTAREA_MIN_HEIGHT = 100;
	const TEXTAREA_MAX_HEIGHT = 250;
	const SEND_BUTTON_SIZE = 40;
	const SEND_BUTTON_OFFSET = 10;
	const TEXT_TO_BUTTON_GAP = 8;
	const TEXT_RIGHT_PADDING = 56;
	const textBottomPadding = SEND_BUTTON_SIZE + SEND_BUTTON_OFFSET + TEXT_TO_BUTTON_GAP;
	const textareaViewportMinHeight = Math.max(40, TEXTAREA_MIN_HEIGHT - textBottomPadding);
	const textareaViewportMaxHeight = Math.max(textareaViewportMinHeight, TEXTAREA_MAX_HEIGHT - textBottomPadding);

	const currentValue = isControlled ? String(value || '') : internalValue;

	// Récupère toutes les formules LaTeX du texte
	const getLatexFormulasInText = (text) => {
		const latexRegex = /(\$\$[\s\S]*?\$\$|\$[^\n$]+\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]|\\begin\{[a-zA-Z*]+\}[\s\S]*?\\end\{[a-zA-Z*]+\})/g;
		const formulas = [];
		let match;
		while ((match = latexRegex.exec(text)) !== null) {
			formulas.push({
				start: match.index,
				end: match.index + match[0].length,
				formula: match[0],
			});
		}
		return formulas;
	};

	// Convertit une position BRUTE (dans le texte réel) en position LOGIQUE
	// où chaque formule LaTeX compte pour 1 seul caractère
	const convertBrutoToLogicalPos = (text, brutePos) => {
		const formulas = getLatexFormulasInText(text);
		let logicalPos = 0;
		let currentPos = 0;

		for (const formula of formulas) {
			if (brutePos <= formula.start) {
				// On n'a pas encore atteint cette formule
				logicalPos += brutePos - currentPos;
				return logicalPos;
			}
			
			if (brutePos <= formula.end) {
				// On est dans cette formule -> on compte juste la formule comme 1 char
				logicalPos += formula.start - currentPos + 1;
				return logicalPos;
			}

			// On a dépassé cette formule
			logicalPos += formula.start - currentPos + 1; // avant la formule + 1 pour la formule
			currentPos = formula.end;
		}

		// Après toutes les formules
		logicalPos += brutePos - currentPos;
		return logicalPos;
	};

	// Convertit une position LOGIQUE en position BRUTE
	const convertLogicalToBrutePos = (text, logicalPos) => {
		const formulas = getLatexFormulasInText(text);
		let currentLogicalPos = 0;
		let currentBrutePos = 0;

		for (const formula of formulas) {
			const charsBeforeFormula = formula.start - currentBrutePos;
			
			if (currentLogicalPos + charsBeforeFormula >= logicalPos) {
				// La position logique est avant cette formule
				return currentBrutePos + (logicalPos - currentLogicalPos);
			}

			currentLogicalPos += charsBeforeFormula + 1; // +1 pour la formule qui compte comme 1 char
			currentBrutePos = formula.end;

			if (currentLogicalPos >= logicalPos) {
				// On a atteint/dépassé la position après avoir traversé une formule
				// Placer après la formule
				return formula.end;
			}
		}

		// Après toutes les formules
		return currentBrutePos + (logicalPos - currentLogicalPos);
	};

	// Repositionne le curseur en dehors des formules LaTeX
	const repositionCursorOutsideFormula = (ta, text, targetPos = null) => {
		const formulas = getLatexFormulasInText(text);
		const pos = targetPos ?? ta.selectionStart;

		for (const formula of formulas) {
			// Si curseur est dans une formule
			if (pos > formula.start && pos < formula.end) {
				// Décide si on va avant ou après la formule
				const distToStart = pos - formula.start;
				const distToEnd = formula.end - pos;
				const newPos = distToStart < distToEnd ? formula.start : formula.end;
				
				ta.selectionStart = ta.selectionEnd = newPos;
				return newPos;
			}
		}
		return pos;
	};

	const updateTextareaHeight = useCallback(() => {
		if (!textareaRef.current) return;
		const ta = textareaRef.current;
		const computedStyle = window.getComputedStyle(ta);
		const paddingTop = parseFloat(computedStyle.paddingTop || '0') || 0;
		const paddingBottom = parseFloat(computedStyle.paddingBottom || '0') || 0;
		const verticalPadding = paddingTop + paddingBottom;
		const minContentHeight = Math.max(textareaViewportMinHeight - verticalPadding, 0);
		const maxContentHeight = Math.max(textareaViewportMaxHeight - verticalPadding, 0);
		ta.style.height = 'auto';
		const rawContentHeight = Math.max(ta.scrollHeight - verticalPadding, 0);
		const clampedContentHeight = Math.min(Math.max(rawContentHeight, minContentHeight), maxContentHeight);
		const viewportHeight = clampedContentHeight + verticalPadding;
		const newHeight = viewportHeight + textBottomPadding;
		ta.style.height = `${viewportHeight}px`;
		setTextareaHeight(newHeight);
		setIsTextareaOverflowing(rawContentHeight > maxContentHeight);
	}, [textBottomPadding, textareaViewportMaxHeight, textareaViewportMinHeight]);

	useEffect(() => {
		updateTextareaHeight();
	}, [currentValue, updateTextareaHeight]);

	// Vérifie et corrige la position du curseur après chaque changement de valeur
	useEffect(() => {
		const ta = textareaRef.current;
		if (!ta) return;

		// Utiliser requestAnimationFrame pour attendre que React ait fini de mettre à jour le DOM
		const raf = requestAnimationFrame(() => {
			const currentPos = ta.selectionStart;
			const text = ta.value;
			const formulas = getLatexFormulasInText(text);

			// Vérifier si le curseur est mal positionné (dedans une formule)
			let needsRepositioning = false;
			for (const formula of formulas) {
				if (currentPos > formula.start && currentPos < formula.end) {
					needsRepositioning = true;
					break;
				}
			}

			if (needsRepositioning) {
				repositionCursorOutsideFormula(ta, text);
			}
		});

		return () => cancelAnimationFrame(raf);
	}, [currentValue]);

	useEffect(() => {
		updateTextareaHeight();
	}, []);

	useEffect(() => {
		if (!isControlled) return;
		const contains = chatInputContainsLatex(value || '');
		setHasLatex(contains);
		if (contains) {
			setInterpretationTick((tick) => tick + 1);
		}
		updateTextareaHeight();
	}, [isControlled, value, updateTextareaHeight]);

	const handleInputChange = (event) => {
		const nextValue = String(event?.target?.value || '');

		if (!isControlled) {
			setInternalValue(nextValue);
		}

		const containsLatex = chatInputContainsLatex(nextValue);
		setHasLatex(containsLatex);
		if (containsLatex) {
			setInterpretationTick((tick) => tick + 1);
		}

		if (typeof onChange === 'function') {
			onChange(nextValue, event);
		}

		updateTextareaHeight();
	};

	// Repositionne le curseur après chaque frappe de touche normale
	const handleKeyUp = (event) => {
		const ta = textareaRef.current;
		if (!ta) return;

		// Ignore les touches de contrôle
		if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'ArrowUp' || event.key === 'ArrowDown' ||
			event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Shift' || event.key === 'Control' || 
			event.key === 'Alt' || event.key === 'Meta') {
			return;
		}

		// Après une frappe normale, repositionne le curseur hors des formules
		// Utiliser un délai plus long pour s'assurer que React a fini de mettre à jour
		setTimeout(() => {
			const formulas = getLatexFormulasInText(ta.value);
			const currentPos = ta.selectionStart;

			// Vérifier si le curseur est dedans une formule
			for (const formula of formulas) {
				if (currentPos > formula.start && currentPos < formula.end) {
					repositionCursorOutsideFormula(ta, ta.value);
					return;
				}
			}
		}, 1);
	};

	// Détecte et supprime les formules LaTeX entièrement
	const handleKeyDown = (event) => {
		const ta = textareaRef.current;
		if (!ta) return;

		const isBackspace = event.key === 'Backspace';
		const isDelete = event.key === 'Delete';
		const isArrowLeft = event.key === 'ArrowLeft';
		const isArrowRight = event.key === 'ArrowRight';

		const text = currentValue;
		const cursorPos = ta.selectionStart;
		const formulas = getLatexFormulasInText(text);

		// Gère les flèches gauche/droite pour naviguer d'une UNITÉ LOGIQUE à la fois
		// (1 formule = 1 unité, 1 caractère normal = 1 unité)
		if (isArrowLeft || isArrowRight) {
			event.preventDefault();
			
			// Convertir position brute en position logique
			const logicalPos = convertBrutoToLogicalPos(text, cursorPos);
			
			// Calculer la nouvelle position logique
			let newLogicalPos;
			if (isArrowRight) {
				newLogicalPos = Math.min(logicalPos + 1, convertBrutoToLogicalPos(text, text.length));
			} else {
				newLogicalPos = Math.max(logicalPos - 1, 0);
			}
			
			// Convertir back en position brute
			const newBrutePos = convertLogicalToBrutePos(text, newLogicalPos);
			ta.selectionStart = ta.selectionEnd = newBrutePos;
			return;
		}

		if (!isBackspace && !isDelete) {
			// Gère aussi Enter + Shift pour envoyer
			if (event.key === 'Enter' && !event.shiftKey) {
				event.preventDefault();
				handleSubmit();
			}
			return;
		}

		let foundFormula = null;

		// Cherche quelle formule contient ou touche le curseur
		for (const formula of formulas) {
			// Backspace: supprimer si curseur est juste après la formule ou dedans
			if (isBackspace && cursorPos > formula.start && cursorPos <= formula.end) {
				foundFormula = formula;
				break;
			}

			// Delete: supprimer si curseur est juste avant la formule ou dedans
			if (isDelete && cursorPos >= formula.start && cursorPos < formula.end) {
				foundFormula = formula;
				break;
			}
		}

		if (foundFormula) {
			event.preventDefault();
			const newText = text.slice(0, foundFormula.start) + text.slice(foundFormula.end);
			
			if (!isControlled) {
				setInternalValue(newText);
			}

			const containsLatex = chatInputContainsLatex(newText);
			setHasLatex(containsLatex);
			if (containsLatex) {
				setInterpretationTick((tick) => tick + 1);
			}

			if (typeof onChange === 'function') {
				onChange(newText);
			}

			updateTextareaHeight();

			// Repositionne le curseur après la suppression
			setTimeout(() => {
				ta.selectionStart = ta.selectionEnd = foundFormula.start;
				ta.focus();
			}, 0);
		}
	};

	const handleSubmit = () => {
		if (typeof onSubmit !== 'function') return;
		onSubmit(currentValue);
	};

	return (
		<>
			<style>{`
				.ai-chat-input-textarea::-webkit-scrollbar {
					width: 6px;
				}
				.ai-chat-input-textarea::-webkit-scrollbar-track {
					background: transparent;
				}
				.ai-chat-input-textarea::-webkit-scrollbar-thumb {
					background: ${hexToRgba(safeAccent, 0.48, safeAccent)};
					border-radius: 3px;
					border: 2px solid transparent;
					background-clip: content-box;
					transition: background 180ms ease;
				}
				.ai-chat-input-textarea::-webkit-scrollbar-thumb:hover {
					background: ${hexToRgba(safeAccent, 0.72, safeAccent)};
					background-clip: content-box;
				}
				.ai-chat-input-textarea::selection {
					background-color: ${hexToRgba(safeAccent, 0.32, safeAccent)};
					color: transparent;
				}
				.ai-chat-input-textarea::-moz-selection {
					background-color: ${hexToRgba(safeAccent, 0.32, safeAccent)};
					color: transparent;
				}
			`}</style>
			<div
			style={{
				width: '100%',
				position: 'relative',
				height: `${textareaHeight + 20}px`,
				padding: '10px 12px',
				borderRadius: 14,
				border: `2px solid ${isInputFocused ? hexToRgba(safeAccent, 0.82, safeAccent) : hexToRgba(safeAccent, 0.56, safeAccent)}`,
				background: isInputFocused
					? hexToRgba(safeBgPrimary, 0.78, safeBgPrimary)
					: hexToRgba(safeBgPrimary, 0.64, safeBgPrimary),
				boxSizing: 'border-box',
				overflow: 'visible',
				boxShadow: isInputFocused
					? `0 14px 26px ${hexToRgba(safeBgPrimary, 0.46, safeBgPrimary)}, 0 0 0 1px ${hexToRgba(safeAccent, 0.34, safeAccent)} inset`
					: `0 8px 18px ${hexToRgba(safeBgPrimary, 0.38, safeBgPrimary)}`,
				transition: 'box-shadow 220ms ease, border-color 220ms ease, background 220ms ease',
				...style,
			}}
		>
			{hasLatex && currentValue.trim() ? (
				<div
					style={{
						position: 'absolute',
						top: 10,
						left: 12,
						right: 12,
						bottom: textBottomPadding,
						color: safeTextPrimary,
						pointerEvents: 'none',
						overflow: 'hidden',
						zIndex: 2,
						boxSizing: 'border-box',
					}}
				>
					<div
						style={{
							width: '100%',
							height: '100%',
							paddingRight: `${TEXT_RIGHT_PADDING}px`,
							boxSizing: 'border-box',
							whiteSpace: 'pre-wrap',
							wordWrap: 'break-word',
							wordBreak: 'break-word',
							fontSize: '0.96rem',
							lineHeight: 1.5,
							fontFamily: 'inherit',
							overflow: 'hidden',
						}}
					>
						<MathContentLayer
							key={`chat-input-latex-${interpretationTick}`}
							content={currentValue}
							style={{
								width: '100%',
								minHeight: '100%',
								whiteSpace: 'pre-wrap',
								wordWrap: 'break-word',
								wordBreak: 'break-word',
								fontSize: '0.96rem',
								lineHeight: 1.5,
								fontFamily: 'inherit',
							}}
						/>
					</div>
				</div>
			) : null}

			<textarea
				ref={textareaRef}
				value={currentValue}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onKeyUp={handleKeyUp}
				onFocus={() => setIsInputFocused(true)}
				onBlur={() => setIsInputFocused(false)}
				spellCheck={false}
				autoCorrect="off"
				autoCapitalize="off"
				data-gramm="false"
				placeholder={placeholder}
				maxLength={maxLength}
				style={{
					position: 'relative',
					zIndex: 3,
					width: '100%',
					height: `${Math.max(textareaViewportMinHeight, textareaHeight - textBottomPadding)}px`,
					minHeight: textareaViewportMinHeight,
					maxHeight: textareaViewportMaxHeight,
					resize: 'none',
					padding: `0 ${TEXT_RIGHT_PADDING}px 0 0`,
					margin: 0,
					border: 'none',
					background: 'transparent',
					color: hasLatex && currentValue.trim() ? 'transparent' : safeTextPrimary,
					caretColor: safeTextPrimary,
					outline: 'none',
					fontSize: '0.96rem',
					lineHeight: 1.5,
					fontFamily: 'inherit',
					boxSizing: 'border-box',
					overflowY: isTextareaOverflowing ? 'auto' : 'hidden',
					scrollPaddingBottom: '0px',
					scrollbarWidth: 'thin',
					scrollbarColor: `${hexToRgba(safeAccent, 0.48, safeAccent)} transparent`,
				}}
				className="ai-chat-input-textarea"
			/>

			<button
				type="button"
				onClick={handleSubmit}
				onMouseEnter={() => setIsSendHovered(true)}
				onMouseLeave={() => setIsSendHovered(false)}
				onFocus={() => setIsSendHovered(true)}
				onBlur={() => setIsSendHovered(false)}
				aria-label="Send message"
				style={{
					position: 'absolute',
					right: SEND_BUTTON_OFFSET,
					bottom: SEND_BUTTON_OFFSET,
					zIndex: 4,
					width: SEND_BUTTON_SIZE,
					height: SEND_BUTTON_SIZE,
					border: `1px solid ${hexToRgba(safeAccent, isSendHovered ? 0.86 : 0.58, safeAccent)}`,
					background: isSendHovered
						? hexToRgba(safeAccent, 0.34, safeAccent)
						: hexToRgba(safeAccent, 0.24, safeAccent),
					color: safeTextPrimary,
					borderRadius: 12,
					padding: 0,
					display: 'grid',
					placeItems: 'center',
					cursor: 'pointer',
					boxShadow: isSendHovered
						? `0 10px 18px ${hexToRgba(safeBgPrimary, 0.42, safeBgPrimary)}`
						: `0 6px 14px ${hexToRgba(safeBgPrimary, 0.28, safeBgPrimary)}`,
					transform: isSendHovered ? 'translateY(-1px)' : 'translateY(0)',
					transition: 'transform 180ms ease, box-shadow 180ms ease, background 180ms ease, border-color 180ms ease',
				}}
			>
				<FiSend size={16} strokeWidth={2.2} />
			</button>
		</div>
		</>
	);
};

export default AIChatFloatingButton;
